import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../lib/api';
import { Activity, AlertTriangle, ArrowLeft, Filter, RefreshCcw, Search } from 'lucide-react';

const LOG_TYPES = [
  ['', 'All types'],
  ['application', 'Application'],
  ['error', 'Error'],
  ['security', 'Security'],
  ['activity', 'Activity'],
  ['subscription', 'Subscription'],
  ['share', 'Share links'],
];

const SEVERITIES = [
  ['', 'All severities'],
  ['info', 'Info'],
  ['warning', 'Warning'],
  ['error', 'Error'],
  ['critical', 'Critical'],
];

const severityClass = (severity) => {
  if (severity === 'critical') return 'border-red-400/60 text-red-200 bg-red-500/10';
  if (severity === 'error') return 'border-red-500/50 text-red-300 bg-red-500/10';
  if (severity === 'warning') return 'border-orange-400/50 text-orange-300 bg-orange-500/10';
  return 'border-cyan-400/40 text-cyan-300 bg-cyan-400/10';
};

const typeClass = (type) => {
  if (type === 'security') return 'text-red-300';
  if (type === 'subscription') return 'text-yellow-300';
  if (type === 'share') return 'text-cyan-300';
  if (type === 'error') return 'text-orange-300';
  return 'text-zinc-300';
};

export default function AdminLogs() {
  const [logs, setLogs] = useState([]);
  const [meta, setMeta] = useState({ currentPage: 1, totalPages: 1, totalLogs: 0, limit: 50 });
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    severity: '',
    user_id: '',
    account_id: '',
    date_from: '',
    date_to: '',
  });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/admin/logs', {
        params: { ...filters, page, limit: 50 },
      });
      setLogs(data.logs || []);
      setMeta({
        currentPage: data.currentPage || 1,
        totalPages: data.totalPages || 1,
        totalLogs: data.totalLogs || 0,
        limit: data.limit || 50,
      });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load logs.');
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const updateFilter = (key, value) => {
    setFilters(cur => ({ ...cur, [key]: value }));
    setPage(1);
  };

  const applyFilters = (e) => {
    e.preventDefault();
    if (page === 1) fetchLogs();
    else setPage(1);
  };

  const clearFilters = () => {
    setFilters({ search: '', type: '', severity: '', user_id: '', account_id: '', date_from: '', date_to: '' });
    setPage(1);
  };

  const start = meta.totalLogs ? ((meta.currentPage - 1) * meta.limit) + 1 : 0;
  const end = meta.totalLogs ? Math.min(meta.currentPage * meta.limit, meta.totalLogs) : 0;

  return (
    <Layout hideFooter>
      <section className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-cyan-300 mb-1">// admin logs</div>
            <h1 className="font-mono-display text-3xl text-white flex items-center gap-3">
              <Activity className="w-7 h-7 text-orange-300" />
              Application Logs
            </h1>
            <p className="text-xs text-zinc-500 mt-2">Sanitized application, security, subscription, account, and share-link events.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchLogs} disabled={loading} className="btn-ghost disabled:opacity-50">
              <RefreshCcw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <Link to="/secure-portal-9x47/console" className="btn-ghost">
              <ArrowLeft className="w-3.5 h-3.5" />
              Admin
            </Link>
          </div>
        </div>

        {error && (
          <div className="mb-4 flex items-start gap-2 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded p-3">
            <AlertTriangle className="w-4 h-4 mt-0.5" />
            {error}
          </div>
        )}

        <form onSubmit={applyFilters} className="neon-card rounded-lg p-4 mb-5">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-cyan-300" />
            <div className="text-[10px] uppercase tracking-widest text-cyan-300">Filters</div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Search</span>
              <div className="mt-1 flex items-center gap-2 bg-black/40 border border-zinc-800 focus-within:border-orange-400/70 rounded px-3 py-2">
                <Search className="w-4 h-4 text-zinc-500" />
                <input value={filters.search} onChange={(e) => updateFilter('search', e.target.value)} placeholder="message, route, user/account id" className="bg-transparent flex-1 min-w-0 outline-none text-sm text-white placeholder:text-zinc-600" />
              </div>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Type</span>
              <select value={filters.type} onChange={(e) => updateFilter('type', e.target.value)} className="mt-1 w-full bg-black/40 border border-zinc-800 rounded px-3 py-2 text-sm text-white">
                {LOG_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Severity</span>
              <select value={filters.severity} onChange={(e) => updateFilter('severity', e.target.value)} className="mt-1 w-full bg-black/40 border border-zinc-800 rounded px-3 py-2 text-sm text-white">
                {SEVERITIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">User ID</span>
              <input value={filters.user_id} onChange={(e) => updateFilter('user_id', e.target.value)} className="mt-1 w-full bg-black/40 border border-zinc-800 rounded px-3 py-2 text-sm text-white outline-none" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Account ID</span>
              <input value={filters.account_id} onChange={(e) => updateFilter('account_id', e.target.value)} className="mt-1 w-full bg-black/40 border border-zinc-800 rounded px-3 py-2 text-sm text-white outline-none" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">From</span>
              <input type="datetime-local" value={filters.date_from} onChange={(e) => updateFilter('date_from', e.target.value)} className="mt-1 w-full bg-black/40 border border-zinc-800 rounded px-3 py-2 text-sm text-white outline-none" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">To</span>
              <input type="datetime-local" value={filters.date_to} onChange={(e) => updateFilter('date_to', e.target.value)} className="mt-1 w-full bg-black/40 border border-zinc-800 rounded px-3 py-2 text-sm text-white outline-none" />
            </label>
            <div className="flex items-end gap-2">
              <button disabled={loading} className="btn-neon disabled:opacity-50">Apply</button>
              <button type="button" onClick={clearFilters} className="btn-ghost">Clear</button>
            </div>
          </div>
        </form>

        <div className="neon-card rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-zinc-500 tabular">Showing {start}-{end} of {meta.totalLogs}</div>
            <div className="text-xs text-zinc-500 tabular">Page {meta.currentPage} of {meta.totalPages}</div>
          </div>
          <div className="divide-y divide-zinc-800">
            {loading && <div className="p-8 text-center text-zinc-500 font-mono-display">Loading logs...</div>}
            {!loading && logs.length === 0 && <div className="p-8 text-center text-zinc-500 font-mono-display">No logs found.</div>}
            {!loading && logs.map(log => (
              <div key={log.id} className="p-4 bg-black/20 hover:bg-black/30 transition-colors">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`inline-flex px-2 py-0.5 rounded border text-[10px] uppercase tracking-widest ${severityClass(log.severity)}`}>{log.severity}</span>
                      <span className={`text-[10px] uppercase tracking-widest ${typeClass(log.type)}`}>{log.type}</span>
                      {log.statusCode && <span className="text-[10px] uppercase tracking-widest text-zinc-500">HTTP {log.statusCode}</span>}
                    </div>
                    <div className="text-sm text-white font-mono-display break-words">{log.message}</div>
                    <div className="mt-1 text-xs text-zinc-500 break-all">
                      {[log.method, log.route].filter(Boolean).join(' ')}
                    </div>
                  </div>
                  <div className="text-xs text-zinc-500 tabular whitespace-nowrap">
                    {log.createdAt ? new Date(log.createdAt).toLocaleString() : 'Unknown time'}
                  </div>
                </div>
                <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-2 text-xs">
                  {log.userId && <div className="rounded border border-zinc-800 bg-black/30 px-2 py-1 text-zinc-400 break-all">User: {log.userId}</div>}
                  {log.accountId && <div className="rounded border border-zinc-800 bg-black/30 px-2 py-1 text-zinc-400 break-all">Account: {log.accountId}</div>}
                  {log.metadata && Object.keys(log.metadata).length > 0 && (
                    <div className="rounded border border-zinc-800 bg-black/30 px-2 py-1 text-zinc-400 break-all sm:col-span-2 lg:col-span-3">
                      Metadata: {JSON.stringify(log.metadata)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="px-4 py-3 border-t border-zinc-800 flex items-center justify-between gap-3 flex-wrap">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={meta.currentPage <= 1 || loading} className="btn-ghost disabled:opacity-40 disabled:cursor-not-allowed">Previous</button>
            <div className="text-xs text-zinc-400 tabular">Page {meta.currentPage} of {meta.totalPages}</div>
            <button onClick={() => setPage(p => Math.min(meta.totalPages, p + 1))} disabled={meta.currentPage >= meta.totalPages || loading} className="btn-ghost disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
